import { cvnaCategoryNamesSortOrder } from '@/vdp/FeaturesAndHighlights/constants';
import CvnaTier from '~/constants/vdp/cvnaTier';
import { useGetVehicleDetails } from '~/context/vdp/VehicleContext';
import type { CvnaCategory, CvnaFeature } from '~/contracts/vdp';

const equivalentFeatures: Record<string, string> = {
  'Heated Seats': 'Heated and Ventilated Seats',
  'Tow Hitch': 'Tow Hitch and Connector',
  'Rear View Camera': '360 Camera',
  'Front View Camera': '360 Camera',
  'Side View Cameras': '360 Camera',
  'Tilt Wheel': 'Power Adjustable Steering Column',
  'Traction Control': 'Intelligent Traction Control',
  'Stability Control': 'Electronic Stability Control',
  'Integrated Garage Door Opener': 'HomeLink'
};
const possiblePreferredFeatures = new Set(Object.values(equivalentFeatures));

function isPossiblePreferredFeature(
  v: CvnaFeature
): v is CvnaFeature & Required<Pick<CvnaFeature, 'name'>> {
  return possiblePreferredFeatures.has(v.name ?? '');
}

export function useGetFilteredCvnaCategories() {
  const { cvnaCategories = [] } = useGetVehicleDetails();

  const actualPreferredFeatures = cvnaCategories.reduce<string[]>(
    (acc, category) =>
      acc.concat(
        (category.cvnaFeatures ?? [])
          .filter(isPossiblePreferredFeature)
          .map(feature => feature.name)
      ),
    []
  );

  return cvnaCategories.map<CvnaCategory>(category => ({
    ...category,
    cvnaFeatures: (category.cvnaFeatures ?? []).filter(
      feature =>
        !actualPreferredFeatures.includes(
          equivalentFeatures[feature.name ?? '']
        )
    )
  }));
}

export function useGetOrderedCvnaCategories() {
  const cvnaCategories = useGetFilteredCvnaCategories();

  return cvnaCategories.sort((a, b) => {
    const orderOfA = a.name ? cvnaCategoryNamesSortOrder.indexOf(a.name) : -1;
    const orderOfB = b.name ? cvnaCategoryNamesSortOrder.indexOf(b.name) : -1;

    return orderOfA - orderOfB;
  });
}

export function useGetStandoutTierCategories() {
  const categories = useGetOrderedCvnaCategories();

  return categories.filter(({ cvnaFeatures = [] }) =>
    cvnaFeatures.some(({ tier }) => tier === CvnaTier.standout)
  );
}
